/**
 * CSV-eksport: UTF-8 med BOM, semikolon, norsk desimalformat.
 * Metadata (periode, filtre, scenario, genereringstid, demomerking) legges som #-linjer først.
 * Celler som kan tolkes som formler nøytraliseres.
 */
import { csvNumber } from '../domain/numbers';

export interface CsvColumn<T> {
  key: string;
  header: string;
  unit?: string;
  get: (row: T) => string | number | null | undefined;
  decimals?: number;
}

export interface CsvMeta {
  title: string;
  period: string;
  comparison?: string;
  filters: string[];
  scenario: string;
  generatedAt: string;
  role: string;
  extra?: string[];
}

const DANGEROUS = /^[=+\-@\t\r]/;

/** Nøytraliserer celler som kan tolkes som formler i regneark (CSV-injeksjon). */
export function sanitizeCell(v: string): string {
  let s = v;
  if (DANGEROUS.test(s)) s = `'${s}`;
  return s;
}

export function escapeCell(v: string): string {
  const s = sanitizeCell(v);
  if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function cellValue(v: string | number | null | undefined, decimals?: number): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return csvNumber(v, decimals);
  return escapeCell(String(v));
}

export function buildCsv<T>(rows: T[], columns: CsvColumn<T>[], meta: CsvMeta): string {
  const lines: string[] = [];
  lines.push(`# Hardanger Rock produksjonsportal – ${meta.title}`);
  lines.push('# Datagrunnlag: eksempeldata (se Datakilder). Ikke kundens driftstall.');
  lines.push(`# Periode: ${meta.period}`);
  if (meta.comparison) lines.push(`# Sammenligning: ${meta.comparison}`);
  lines.push(`# Scenario: ${meta.scenario}`);
  lines.push(`# Filtre: ${meta.filters.length ? meta.filters.join(' | ') : 'ingen'}`);
  lines.push(`# Rolle: ${meta.role}`);
  lines.push(`# Generert: ${meta.generatedAt} (fast referansetidspunkt)`);
  lines.push('# Format: UTF-8 med BOM; semikolon som skilletegn; komma som desimaltegn; enheter i kolonneoverskrift');
  for (const e of meta.extra ?? []) lines.push(`# ${e}`);
  lines.push(columns.map((c) => escapeCell(c.unit ? `${c.header} (${c.unit})` : c.header)).join(';'));
  for (const r of rows) {
    lines.push(columns.map((c) => cellValue(c.get(r), c.decimals)).join(';'));
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

export function downloadText(filename: string, content: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
